import { useCallback, useEffect, useRef, useState } from "react";
import { fmtCode, partFrames, processFrames } from "../data/format";
import { motion, AnimatePresence } from "motion/react";
import {
  AlertTriangle, Check, Clock, Download, ImagePlus, Loader2, Maximize2, MessageSquare, RotateCcw, ThumbsDown,
  ThumbsUp,
} from "lucide-react";
import { fetchPoster, posterIn, sendVisualFeedback, sized } from "../api/client";
import { Crop, Exploded, FrameViewer, PartRow, PartsViewer, PosterViewer } from "./VisualViewer";

/**
 * Fotodan tovar tasviri — suhbatdagi javob sifatida.
 *
 * Natija serverda chizilgan poster emas, JSON + yozuvsiz rasm: raqamlar
 * shu yerda chiziladi. Rasm, raqam yoki qism bosilsa katta ko'rinish
 * ochiladi (VisualViewer.jsx). Tayyor poster — yuklab olish uchun.
 */

// Kutish kartasi: bosqichga qarab chapda o'z animatsiyasi (index.css `.vz-*`),
// tovar aniqlangach sarlavha — tovar nomi. Prototip: frontend/prototypes/tasvir-kutish.html
const STAGES = {
  uploading: { title: "Foto yuklanmoqda", step: 0, scene: "up" },
  queued: { title: "Navbat kutilmoqda", step: 0, scene: "queue" },
  analyzing: { title: "Tovar aniqlanmoqda", step: 1, scene: "scan" },
  coding: { title: "TIF TN kodlari aniqlanmoqda", status: "TIF TN kodlari aniqlanmoqda", step: 2, scene: "code" },
  drawing: { title: "Tasvir chizilmoqda", status: "Qismlarga ajratib chizilmoqda", step: 2, scene: "draw" },
  locating: { title: "Qismlar belgilanmoqda", status: "Qismlar raqamlanmoqda", step: 3, scene: "mark" },
  composing: { title: "Poster tayyorlanmoqda", status: "Poster tayyorlanmoqda", step: 4, scene: "pose" },
};

const LANGS = [
  ["uz", "O'zbekcha"],
  ["oz", "Ўзбекча"],
  ["ru", "Русский"],
  ["en", "English"],
];

const time = (at) =>
  new Date(at || Date.now()).toLocaleTimeString("uz-UZ", { hour: "2-digit", minute: "2-digit" });

export function VisualCard({ message, onRetry, onDraw }) {
  const { view, stage, photo, product, at, focus } = message;
  const ref = useRef(null);
  const done = view?.status === "done";

  // Natija tayyor bo'lganda kartaning boshi ko'rinsin, oxiri emas. Suhbat ma'lum tasvir uchun
  // ochilgan bo'lsa (`focus`) faqat o'sha suriladi
  useEffect(() => {
    if (done && focus !== false) ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [done, focus]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      className="mb-8 scroll-mt-4 space-y-3"
      data-job={view?.id ?? message.jobId}
    >
      <div className="flex items-center justify-between gap-2 text-xs">
        <div className="flex items-center gap-2.5">
          <div className="flex h-6 w-6 items-center justify-center rounded-lg border border-sky-400/40 bg-sky-950/80 p-0.5">
            <img src="/logo.png" alt="" className="h-full w-full object-contain" />
          </div>
          <span className="text-xs font-bold tracking-wide text-white sm:text-sm">AIExpert</span>
        </div>
        <div className="flex items-center gap-2 font-mono text-[11px] text-sky-400/60">
          {done && view.usage?.ms > 0 && (
            <span className="flex items-center gap-1 rounded border border-sky-900/50 bg-[#031329]/60 px-1.5 py-0.5 text-sky-300/80">
              <Clock className="h-3 w-3 text-sky-400" />
              {Math.round(view.usage.ms / 1000)}s
            </span>
          )}
          <span>{time(at)}</span>
        </div>
      </div>

      {done ? (
        view.drawn === false ? <Summary view={view} onDraw={() => onDraw?.(message)} /> : <Result view={view} />
      ) : view?.status === "failed" ? (
        <Failed error={view.error} onRetry={() => onRetry?.(message)} canRetry={canRetry(message)} />
      ) : (
        <Progress stage={stage} photo={photo} product={product} draw={message.draw ?? view?.draw ?? true} />
      )}
    </motion.div>
  );
}

// Qayta urinish foyda beradigan xatolar (server bilan bir xil); kodsiz — tarmoq xatosi
const RETRYABLE = ["busy", "provider_error", "billing", undefined];

const canRetry = (m) =>
  RETRYABLE.includes(m.view?.error?.code) && (Boolean(m.view?.id) || Boolean(m.files?.length));

function Failed({ error, onRetry, canRetry }) {
  const photoProblem = ["not_a_product", "bad_photo"].includes(error?.code);
  return (
    <div role="alert" className="flex flex-wrap items-center gap-3 rounded-2xl border border-rose-800/50 bg-rose-950/40 p-4">
      <AlertTriangle className="h-5 w-5 shrink-0 text-rose-300" />
      <p className="min-w-0 flex-1 text-sm text-rose-100">
        {error?.message || "Tahlil qilib bo'lmadi"}
        {photoProblem && <span className="block text-rose-200/70">Boshqa foto yuboring.</span>}
      </p>
      {canRetry && (
        <button
          onClick={onRetry}
          className="flex shrink-0 items-center gap-2 rounded-xl bg-rose-500/90 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-rose-500"
        >
          <RotateCcw className="h-4 w-4" /> Qayta urinish
        </button>
      )}
    </div>
  );
}

function Progress({ stage, photo, product, draw }) {
  // Faqat tahlil: yuklash, aniqlash, kodlar; rasm bilan: + chizish, belgilash, poster
  const STEPS = draw ? 5 : 3;
  // Rasmda ortiqcha yozuv chiqsa bosqich `drawing` ga qaytadi (12 tovardan 3 tasida)
  const located = useRef(false);
  if (stage === "locating") located.current = true;
  const redraw = stage === "drawing" && located.current;

  const s = STAGES[stage] ?? STAGES.queued;
  const named = s.step >= 2 && product;
  const title = named ? product : `${s.title}…`;
  const status = named ? `${redraw ? "Tasvir aniqlashtirilmoqda" : s.status}…` : "";

  return (
    <div className="vz relative overflow-hidden rounded-2xl border border-sky-500/30 bg-gradient-to-r from-[#061c3b]/95 via-[#08274f]/90 to-[#061c3b]/95 p-4 shadow-lg shadow-sky-950/40 sm:p-5">
      <div className="flex items-center gap-4 sm:gap-5">
        <div className="vz-tile relative h-20 w-20 shrink-0 overflow-hidden rounded-[14px] sm:h-24 sm:w-24">
          <AnimatePresence initial={false}>
            <motion.div
              key={s.scene}
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.06 }}
              transition={{ duration: 0.35 }}
              className="absolute inset-0"
            >
              <Scene name={s.scene} photo={photo} />
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="min-w-0 flex-1">
          <Swap id={title} className="flex items-center gap-2 text-base font-bold leading-tight text-white sm:text-lg">
            {named && (
              <span className="grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full bg-[#9ece6a]/15 text-[#9ece6a]">
                <Check className="h-3 w-3" strokeWidth={3} />
              </span>
            )}
            <span className="min-w-0">{title}</span>
          </Swap>
          {status && (
            <Swap id={status} className="mt-1 text-[13.5px] text-sky-100/85">
              {status}
            </Swap>
          )}

          <div className="mt-3.5 flex items-center" aria-hidden="true">
            {Array.from({ length: STEPS }, (_, i) => (
              <span key={i} className="contents">
                <span className={`vz-dot ${i < s.step ? "done" : i === s.step ? "now" : ""}`} />
                {i < STEPS - 1 && <span className={`vz-seg ${i < s.step ? "done" : i === s.step ? "now" : ""}`} />}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Matn almashuvi: eskisi yuqoriga chiqib so'nadi, yangisi pastdan kiradi
function Swap({ id, className, children }) {
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={id}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={{ duration: 0.2 }}
        className={className}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}

// Bosqich sahnalari, 80x80 koordinatada
function Scene({ name, photo }) {
  if (name === "scan") {
    return (
      <>
        {photo ? (
          <img src={photo} alt="" className="h-full w-full object-cover" />
        ) : (
          <svg viewBox="0 0 80 80" className="h-full w-full">
            <rect className="vz-ln" x="18" y="22" width="44" height="36" rx="6" />
            <path className="vz-ln" d="M22 54 l11-11 7 7 6-6 12 12" />
          </svg>
        )}
        <div className="vz-scan" />
      </>
    );
  }
  const svg = (cls, children) => (
    <svg viewBox="0 0 80 80" className={`h-full w-full ${cls}`}>
      {children}
    </svg>
  );
  if (name === "up") {
    return svg("vz-up", <>
      <rect className="vz-ln" x="18" y="36" width="44" height="30" rx="6" />
      <path className="vz-ln" d="M22 62 l11-11 7 7 6-6 12 12" />
      <circle className="vz-fill-line" cx="30" cy="45" r="3" />
      {[30, 47, 38.5].map((x) => (
        <rect key={x} className="bit vz-fill-accent" x={x} y="30" width="3" height="3" rx="1" />
      ))}
      <g className="arrow">
        <path className="vz-ln" style={{ stroke: "var(--vz-accent)", strokeWidth: 3 }} d="M40 28 V12 M33 19 l7-7 7 7" />
      </g>
    </>);
  }
  if (name === "queue") {
    return svg("vz-queue", <>
      <defs>
        <clipPath id="vz-qc"><rect x="18" y="0" width="62" height="80" /></clipPath>
      </defs>
      <g clipPath="url(#vz-qc)">
        <g className="belt">
          {[20, 38, 56, 74].map((x, i) => (
            <rect key={x} x={x} y="32" width="12" height="16" rx="4" strokeWidth="1.5"
              style={{ fill: i ? "var(--vz-soft)" : "var(--vz-accent)", stroke: "var(--vz-line)" }} />
          ))}
        </g>
      </g>
      <line className="gate" x1="14" y1="24" x2="14" y2="56" strokeWidth="3" strokeLinecap="round" />
      <path className="vz-ln" d="M18 60 H66" style={{ strokeWidth: 2, opacity: 0.35 }} />
    </>);
  }
  if (name === "draw") {
    return svg("vz-draw", <>
      <path className="vz-ln p1" pathLength="1" d="M32 14 H48 V22 H32 Z" />
      <path className="vz-ln p2" pathLength="1" d="M30 30 H50 Q56 30 56 36 V52 Q56 58 50 58 H30 Q24 58 24 52 V36 Q24 30 30 30 Z" />
      <path className="vz-ln p4" pathLength="1" d="M32 38 H48 V50 H32 Z" style={{ strokeWidth: 2, opacity: 0.7 }} />
      <path className="vz-ln p3" pathLength="1" d="M22 68 H58" />
      <g className="pen">
        <circle className="vz-fill-accent" r="3" />
        <circle r="6" fill="none" strokeWidth="1" opacity="0.5" style={{ stroke: "var(--vz-accent)" }} />
      </g>
    </>);
  }
  if (name === "mark") {
    return svg("vz-mark", <>
      <path className="vz-ln part" d="M24 10 H38 V17 H24 Z" />
      <path className="vz-ln part" d="M22 25 H40 Q45 25 45 30 V46 Q45 51 40 51 H22 Q17 51 17 46 V30 Q17 25 22 25 Z" />
      <path className="vz-ln part" d="M15 64 H47" />
      {[[31, 13.5, 64, 14, 1], [38, 38, 64, 40, 2], [40, 64, 64, 64, 3]].map(([ax, ay, cx, cy, n]) => (
        <g key={n}>
          <path className={`lead n${n}`} pathLength="1" d={`M${ax} ${ay} L${cx - 7} ${cy}`} fill="none" strokeWidth="1.5"
            style={{ stroke: "var(--vz-line)" }} />
          <circle className="vz-fill-accent" cx={ax} cy={ay} r="2.2" />
          <g className={`mk n${n}`}>
            <circle cx={cx} cy={cy} r="7" fill="#fff" strokeWidth="2" style={{ stroke: "var(--vz-accent)" }} />
            <text x={cx} y={cy + 3} fontSize="8.5" fontWeight="700" fontFamily="Arial" textAnchor="middle" fill="#0f172a">{n}</text>
          </g>
        </g>
      ))}
    </>);
  }
  if (name === "code") {
    // qism nomlari yonida kod yorliqlari birin-ketin paydo bo'ladi
    return svg("vz-pose", <>
      {[20, 36, 52].map((y, i) => (
        <g key={y}>
          <rect className="vz-fill-line" x="10" y={y} width="28" height="5" rx="2.5" opacity="0.55" />
          <rect className={`blk b${i + 2}`} x="44" y={y - 3.5} width="26" height="12" rx="3" strokeWidth="1.2"
            style={{ fill: "var(--vz-soft)", stroke: "var(--vz-accent)" }} />
        </g>
      ))}
    </>);
  }
  return svg("vz-pose", <>
    <rect className="vz-ln" x="10" y="12" width="60" height="56" rx="5" style={{ strokeWidth: 2, opacity: 0.6 }} />
    <rect className="blk b1" x="15" y="18" width="24" height="26" rx="3" strokeWidth="1.2"
      style={{ fill: "var(--vz-soft)", stroke: "var(--vz-line)" }} />
    <rect className="blk b2 vz-fill-line" x="43" y="19" width="22" height="4" rx="2" />
    <rect className="blk b3 vz-fill-line" x="43" y="28" width="18" height="4" rx="2" />
    <rect className="blk b4 vz-fill-line" x="43" y="37" width="20" height="4" rx="2" />
    <g className="blk b5">
      {[15, 28, 41, 54].map((x) => (
        <rect key={x} className="vz-fill-accent" x={x} y="50" width="11" height="12" rx="2" opacity="0.55" />
      ))}
    </g>
  </>);
}

/** Faqat tahlil: nom, kodlar, qismlar, jarayon; rasm va poster — tugma bilan. */
function Summary({ view, onDraw }) {
  return (
    <div className="space-y-5 rounded-2xl border border-sky-500/25 bg-gradient-to-b from-[#061e42]/90 to-[#04142d]/95 p-4 shadow-xl shadow-sky-950/40 sm:p-6">
      <div>
        <h3 className="text-xl font-bold text-white sm:text-2xl">{view.product?.name}</h3>
        <p className="mt-1 text-sm text-sky-100/80">{view.product?.summary}</p>
        <ProductCodes codes={view.product?.codes} />
      </div>

      <ol className="grid gap-x-4 gap-y-1 sm:grid-cols-2">
        {(view.parts ?? []).map((p) => (
          <PartRow key={p.n} part={p} />
        ))}
      </ol>

      {view.process?.length > 0 && (
        <ol className="flex flex-wrap gap-2">
          {view.process.map((s) => (
            <li key={s.n} className="rounded-lg bg-white/5 px-2.5 py-1 text-xs text-sky-100/80">
              {s.n}. {s.name}
            </li>
          ))}
        </ol>
      )}

      {view.uncertain?.length > 0 && (
        <p className="rounded-xl border border-amber-700/40 bg-amber-950/20 px-3 py-2 text-xs text-amber-100/80">
          Fotodan aniqlanmadi: {view.uncertain.join("; ")}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={onDraw}
          className="flex items-center gap-2 rounded-xl bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-sky-600/30 transition-colors hover:bg-sky-500"
        >
          <ImagePlus className="h-4 w-4" /> Rasm va poster chizish
        </button>
        <span className="text-xs text-sky-300/60">~1 daqiqa</span>
        {view.drawError && <span role="alert" className="text-xs text-rose-300">{view.drawError}</span>}
      </div>

      <div className="border-t border-sky-800/40 pt-3">
        <Feedback view={view} />
      </div>
    </div>
  );
}

// Kodni LLM emas, kaskad tanlaydi (src/visual/codes.py) — baribir taxminiy, tasdiq ekspertda
function ProductCodes({ codes }) {
  if (!codes?.length) return null;
  const [main, ...alt] = codes;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <span className="text-xs text-sky-100/70">Taxminiy TIF TN kodi</span>
      <span
        title={main.title}
        className="rounded-lg bg-sky-900/60 px-2.5 py-1 font-mono text-sm font-semibold text-white ring-1 ring-sky-500/40"
      >
        {fmtCode(main.code)}
      </span>
      {alt.map((c) => (
        <span key={c.code} title={c.title} className="rounded-lg px-2 py-1 font-mono text-xs text-sky-300/70 ring-1 ring-sky-800/60">
          {fmtCode(c.code)}
        </span>
      ))}
      <span className="text-[11px] text-amber-300/80">ekspert tasdiqlashi kerak</span>
    </div>
  );
}

function useImageSize(src) {
  const [size, setSize] = useState(null);
  useEffect(() => {
    const img = new Image();
    img.onload = () => setSize({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = src;
    return () => (img.onload = null);
  }, [src]);
  return size;
}

// Kartada 1600 px nusxa (~180 KB): asli 2400 px ~1.7 MB, telefon xotirasida 17 MB.
// Katta ko'rinish aslini oladi.
const CARD_W = 1600;

function Result({ view }) {
  const card = sized(view.files.image, CARD_W);
  const size = useImageSize(card); // faqat nisbat kerak — aslini yuklamaslik uchun
  const [hover, setHover] = useState(null);
  const [viewer, setViewer] = useState(null); // {n} — qismlar oynasi
  const [frames, setFrames] = useState(null); // {items, index} — qism yoki jarayon kadri kattalashtirilgan
  const closeViewer = useCallback(() => setViewer(null), []);
  const closeFrame = useCallback(() => setFrames(null), []);
  const parts = partFrames(view.parts);
  const steps = (view.process ?? []).filter((s) => s.box);

  return (
    <div className="space-y-5 rounded-2xl border border-sky-500/25 bg-gradient-to-b from-[#061e42]/90 to-[#04142d]/95 p-4 shadow-xl shadow-sky-950/40 sm:p-6">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="text-xl font-bold text-white sm:text-2xl">{view.product?.name}</h3>
          <p className="mt-1 text-sm text-sky-100/80">{view.product?.summary}</p>
          <ProductCodes codes={view.product?.codes} />
        </div>
        {size && (
          <button
            onClick={() => setViewer({ n: null })}
            title="Kattalashtirish"
            className="shrink-0 rounded-xl border border-sky-700/50 bg-sky-950/60 p-2 text-sky-200 transition-colors hover:bg-sky-800/60 hover:text-white"
          >
            <Maximize2 className="h-4 w-4" />
          </button>
        )}
      </div>

      {size ? (
        <div
          role="button"
          tabIndex={0}
          onClick={() => setViewer({ n: null })}
          onKeyDown={(e) => e.key === "Enter" && setViewer({ n: null })}
          className="cursor-zoom-in"
        >
          <Exploded
            view={view}
            size={size}
            src={card}
            active={hover}
            onPick={(n) => setViewer({ n })}
            onHover={setHover}
            className="rounded-xl"
          />
        </div>
      ) : (
        <div className="aspect-[4/3] animate-pulse rounded-xl bg-sky-900/30" />
      )}

      <ol className="grid gap-x-4 gap-y-1 sm:grid-cols-2">
        {(view.parts ?? []).map((p) => (
          <PartRow
            key={p.n}
            part={p}
            active={hover === p.n}
            onHover={setHover}
            onClick={() => size && setViewer({ n: p.n })}
            thumb={size && { view, size, src: card }}
            onZoom={(n) => setFrames({ items: parts, index: parts.findIndex((f) => f.key === n) })}
          />
        ))}
      </ol>

      {size && steps.length > 0 && (
        <div className="grid grid-cols-3 gap-2 sm:flex">
          {steps.map((s, i) => (
            <button
              key={s.n}
              onClick={() => setFrames({ items: processFrames(view.process), index: i })}
              className="group min-w-0 space-y-1 text-left sm:flex-1"
            >
              <Crop
                view={view}
                box={s.box}
                src={card}
                className="rounded-lg ring-sky-400/70 transition group-hover:ring-2"
                style={{
                  aspectRatio: `${(s.box[3] - s.box[1]) * size.w} / ${(s.box[2] - s.box[0]) * size.h}`,
                }}
              />
              <span className="block text-[11px] leading-snug text-sky-100/80">
                {s.n}. {s.name}
              </span>
            </button>
          ))}
        </div>
      )}

      {view.uncertain?.length > 0 && (
        <p className="rounded-xl border border-amber-700/40 bg-amber-950/20 px-3 py-2 text-xs text-amber-100/80">
          Fotodan aniqlanmadi: {view.uncertain.join("; ")}
        </p>
      )}

      <Poster view={view} />

      <div className="border-t border-sky-800/40 pt-3">
        <Feedback view={view} />
      </div>

      {/* kartadagi 1600 px nusxa keshda — to'liq 2400 px (~1.7 MB) sekin tarmoqda 6–17 s yuklanardi */}
      {viewer && <PartsViewer view={view} size={size} initial={viewer.n} src={card} onClose={closeViewer} />}
      {frames && frames.index >= 0 && (
        // kartadagi 1600 px nusxa allaqachon yuklangan — kadr darhol ochiladi
        <FrameViewer view={view} size={size} items={frames.items} index={frames.index} src={card} onClose={closeFrame} />
      )}
    </div>
  );
}

const slug = (s) =>
  (s || "tasvir").toLowerCase().replace(/[ʻʼ'`]/g, "").replace(/[^a-z0-9а-яёўқғҳ]+/gi, "-").replace(/^-|-$/g, "");

function Poster({ view }) {
  const [lang, setLang] = useState(view.lang);
  const [loaded, setLoaded] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const close = useCallback(() => setOpen(false), []);

  // Boshqa til birinchi so'ralganda server uni tarjima qilib yig'adi (~3 s)
  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const url = URL.createObjectURL(await fetchPoster(view.files.poster, lang));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${slug(view.product?.name)}-${lang}.jpg`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex gap-3 rounded-2xl border border-sky-800/50 bg-[#041228]/70 p-3">
      <button
        onClick={() => setOpen(true)}
        title="Posterni ko'rish"
        className="group relative w-28 shrink-0 self-center overflow-hidden rounded-xl bg-white ring-sky-400/70 transition hover:ring-2 sm:w-36"
      >
        <img
          src={posterIn(view.files.poster, lang, 480)}
          alt="Poster"
          onLoad={() => setLoaded(lang)}
          onError={() => setError("Poster ochilmadi, qayta urinib ko'ring")}
          className="block aspect-[3/2] w-full object-contain"
        />
        {loaded !== lang && (
          <span className="absolute inset-0 flex items-center justify-center bg-slate-900/40">
            <Loader2 className="h-5 w-5 animate-spin text-white" />
          </span>
        )}
        <span className="absolute bottom-1.5 right-1.5 rounded-md bg-slate-900/70 p-1 text-white">
          <Maximize2 className="h-3.5 w-3.5" />
        </span>
      </button>

      <div className="flex min-w-0 flex-1 flex-col justify-center gap-2">
        <div className="grid grid-cols-2 gap-1 rounded-xl bg-[#020b1a] p-1 sm:grid-cols-4">
          {LANGS.map(([k, v]) => (
            <button
              key={k}
              onClick={() => setLang(k)}
              className={`rounded-lg px-2 py-1.5 text-xs transition-colors ${
                lang === k ? "bg-sky-700 font-semibold text-white" : "text-sky-300/70 hover:text-white"
              }`}
            >
              {v}
            </button>
          ))}
        </div>

        <button
          onClick={save}
          disabled={busy}
          className="flex items-center justify-center gap-2 rounded-xl bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-sky-600/30 transition-colors hover:bg-sky-500 disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Yuklab olish
        </button>

        {error && <p role="alert" className="text-xs text-rose-300">{error}</p>}
      </div>

      {open && (
        <PosterViewer
          title={view.product?.name}
          src={posterIn(view.files.poster, lang, 1600)}
          fullSrc={posterIn(view.files.poster, lang)}
          placeholder={posterIn(view.files.poster, lang, 480)}
          onClose={close}
          onDownload={save}
          busy={busy}
        />
      )}
    </div>
  );
}

const BTN = "flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-[11px] transition-colors disabled:opacity-50";

function Feedback({ view }) {
  const [open, setOpen] = useState(false);
  const [identity, setIdentity] = useState(true);
  const [wrong, setWrong] = useState([]);
  const [comment, setComment] = useState("");
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (payload) => {
    setBusy(true);
    setError("");
    try {
      await sendVisualFeedback(view.id, payload);
      setSaved(true);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  if (saved) {
    return (
      <div className="flex items-center gap-1.5 text-[11px] text-emerald-300">
        <Check className="h-3.5 w-3.5 text-emerald-400" /> Baho yozildi — rahmat
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] text-sky-300/70">Tasvir to'g'rimi?</span>
        <button
          onClick={() => submit({ identity_ok: true, rating: "good" })}
          disabled={busy}
          className={`${BTN} border-emerald-800/50 bg-emerald-950/40 text-emerald-200 hover:bg-emerald-900/50`}
        >
          <ThumbsUp className="h-3.5 w-3.5" /> To'g'ri
        </button>
        <button
          onClick={() => setOpen(true)}
          disabled={busy}
          className={`${BTN} border-rose-800/50 bg-rose-950/40 text-rose-200 hover:bg-rose-900/50`}
        >
          <ThumbsDown className="h-3.5 w-3.5" /> Xato
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="space-y-2.5 rounded-xl border border-sky-800/40 bg-[#030f24]/90 p-3">
              <div className="flex flex-wrap items-center gap-1.5">
                <button
                  onClick={() => setIdentity((v) => !v)}
                  className={`rounded-lg border px-2 py-1 text-[11px] ${
                    identity
                      ? "border-sky-800/50 bg-[#041228] text-sky-300 hover:bg-sky-900/40"
                      : "border-rose-500/60 bg-rose-900/50 text-white"
                  }`}
                >
                  Boshqa tovar
                </button>
                {(view.parts ?? []).map((p) => {
                  const on = wrong.includes(p.n);
                  return (
                    <button
                      key={p.n}
                      title={p.name}
                      onClick={() => setWrong((w) => (on ? w.filter((n) => n !== p.n) : [...w, p.n]))}
                      className={`h-7 w-7 rounded-full text-[11px] font-bold ${
                        on ? "bg-rose-600 text-white" : "border border-sky-800/50 text-sky-200 hover:bg-sky-900/40"
                      }`}
                    >
                      {p.n}
                    </button>
                  );
                })}
              </div>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={2}
                maxLength={2000}
                placeholder="Nima noto'g'ri?"
                className="w-full resize-none rounded-lg border border-sky-800/50 bg-[#041228] px-2 py-1.5 text-[11px] text-white placeholder:text-sky-700 focus:border-sky-500 focus:outline-none"
              />
              {error && <div role="alert" className="text-[11px] text-rose-300">{error}</div>}
              <div className="flex items-center gap-2">
                <button
                  onClick={() =>
                    submit({ identity_ok: identity, wrong_parts: wrong, comment: comment.trim(), rating: "bad" })
                  }
                  disabled={busy}
                  className="flex items-center gap-1.5 rounded-lg border border-sky-500/40 bg-sky-900/60 px-3 py-1 text-[11px] font-medium text-white hover:bg-sky-800/70 disabled:opacity-50"
                >
                  <MessageSquare className="h-3.5 w-3.5" /> {busy ? "Yozilmoqda..." : "Yuborish"}
                </button>
                <button
                  onClick={() => setOpen(false)}
                  className="rounded-lg px-2 py-1 text-[11px] text-sky-400/70 hover:text-sky-200"
                >
                  Bekor qilish
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {!open && error && <div role="alert" className="text-[11px] text-rose-300">{error}</div>}
    </div>
  );
}
